




























































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import BracketTeam from './components/BracketTeam.vue'
import { dragscroll } from 'vue-dragscroll'
import {
  BracketAdjacencyMatrixEntity,
  BracketMatchAdjacencyMatrixEntity,
  BracketMatrix,
  BracketScoreResult,
  TournamentTeamListItemEntity
} from '@/api/stage'

export interface FreeForAllScore {
  tournamentTeamId: string
  name: string
  rank: number
  score: number
}

@Component({
  components: {
    BracketTeam
  },
  directives: {
    dragscroll
  }
})
export default class FreeForAllBracket extends Vue {
  @Prop({ type: Number, default: 2 }) opponentNum!: number

  @Prop({ type: Object, required: true })
  readonly bracket!: BracketAdjacencyMatrixEntity

  @Prop({ type: Boolean, default: false })
  readonly disableDragScroll!: boolean

  hoverTarget: string | null = null
  rounds: BracketMatchAdjacencyMatrixEntity[][] | null = null
  hasAdditionalMatches: boolean = false

  teams: string[] = []

  hoverTeam(teamId: string | null = null) {
    this.hoverTarget = typeof teamId === 'string' ? teamId : null
  }

  /* Hooks */
  created() {
    this.initialize()
  }

  initialize() {
    const bracketMatrix = new BracketMatrix(this.bracket)
    const structure = bracketMatrix.toBracketTreeNoEdge()
    if (!structure) this.rounds = null
    this.rounds = structure

    // generate identifier if necessary
    if (
      this.rounds &&
      this.rounds.flat().filter(m => !!m.identifier && !m.isAdditional)
        .length <= 0
    ) {
      let num = 1
      for (const round of this.rounds) {
        for (const match of round.filter(m => !m.isAdditional)) {
          match.identifier = `G${this.bracket.groupNumber}-M${num}`
          num++
        }
      }

      num = 1
      for (const round of this.rounds) {
        for (const match of round.filter(m => m.isAdditional)) {
          this.hasAdditionalMatches = true

          if (!match.identifier) {
            match.identifier = `G1-AM-${num}`
          }
          num++
        }
      }
    }

    // check if there are any additional matches
    if (structure) {
      this.hasAdditionalMatches = structure
        .flat()
        .some(match => match.isAdditional)
    }
  }

  @Watch('bracket', { immediate: true, deep: true })
  onBracketStructureChanged() {
    this.initialize()
  }

  getTeamScore(
    match: BracketMatchAdjacencyMatrixEntity,
    tournamentTeamId: string
  ) {
    return match.scores.find(
      score => score.tournamentTeamId === tournamentTeamId
    )!
  }

  getTeamTotalScore(
    match: BracketMatchAdjacencyMatrixEntity,
    team: TournamentTeamListItemEntity
  ) {
    // const useWinCount =
    //  match.scores.map(score => score.winCount).reduce((a, b) => a + b, 0) > 1
    const score = this.getTeamScore(match, team.tournamentTeamId)
    if (!score) return 0
    return score.totalScore
  }

  getEmptyTournamentTeamFiller(teams: TournamentTeamListItemEntity[]) {
    if (teams.length <= 0) return this.opponentNum

    return teams.length % this.opponentNum
  }

  getTeamMatchBracketScoreResult(
    match: BracketMatchAdjacencyMatrixEntity,
    team: TournamentTeamListItemEntity
  ) {
    const matchIndex = this.bracket.matches.indexOf(match)

    if (
      matchIndex !== -1 &&
      match.teams.length === 1 &&
      !this.bracket.edges.find(edge => edge[1] === matchIndex)
    ) {
      return BracketScoreResult.Win
    }

    const score = this.getTeamScore(match, team.tournamentTeamId)
    if (!score) return BracketScoreResult.None
    return score.result
  }

  getSortedTeams(match: BracketMatchAdjacencyMatrixEntity) {
    return match.teams.slice().sort((a, b) => {
      const scoreA = match.scores.find(
        score => score.tournamentTeamId === a.tournamentTeamId
      )
      const scoreB = match.scores.find(
        score => score.tournamentTeamId === b.tournamentTeamId
      )

      const scoreATotalScore = scoreA ? scoreA.totalScore : 0
      const scoreBTotalScore = scoreB ? scoreB.totalScore : 0

      const compare = scoreBTotalScore - scoreATotalScore

      if (compare === 0) {
        return a.name.localeCompare(b.name)
      }

      return compare
    })
  }

  getRoundOf(roundMatch: BracketMatchAdjacencyMatrixEntity[]) {
    if (!roundMatch || !roundMatch.length || !roundMatch[0]) return ''
    const roundOf = roundMatch[0].roundOf
    return this.$t('Bracket.RoundOfFFA', { roundOf: roundOf })
  }

  get scores() {
    const matchTeams = this.bracket.matches.flatMap(match => {
      return match.teams.map(team => ({
        tournamentTeamId: team.tournamentTeamId,
        name: team.name,
        score: match.scores.find(
          score => score.tournamentTeamId === team.tournamentTeamId
        )
      }))
    })

    // group teams
    const teams: FreeForAllScore[] = []

    for (const matchTeam of matchTeams) {
      let team = teams.find(
        team => team.tournamentTeamId === matchTeam.tournamentTeamId
      )

      if (!team) {
        team = {
          tournamentTeamId: matchTeam.tournamentTeamId,
          name: matchTeam.name,
          rank: 0,
          score: 0
        }
        teams.push(team)
      }

      if (
        matchTeam!.score &&
        [
          BracketScoreResult.Win,
          BracketScoreResult.Lose,
          BracketScoreResult.Draw
        ].includes(matchTeam!.score.result)
      ) {
        team!.score += matchTeam!.score!.totalScore
      }
    }

    return teams
      .sort((a, b) => {
        const diff = b.score - a.score

        if (diff === 0) {
          return a.name.localeCompare(b.name)
        }

        return diff
      })
      .map((item, index) => ({
        ...item,
        rank: index + 1
      }))
  }
}
